import type { AssetDto } from 'src/store/generated/leela-api-generated';
import React from 'react';

/**
 * Extracts and formats the filename from an asset object.
 * If the filename (without extension) is a hexadecimal string (the case when it's a Bender generated asset, returns
 * "system-generated" in italics.
 * Otherwise, returns the decoded filename from the asset URL.
 *
 * @param asset - The asset object containing the assetUrl property
 * @param t - Translation function
 * @returns The formatted filename as a string or React element
 */

export const extractFilenameFromAsset = (asset: AssetDto, t: (key: string) => string) => {
  // First decode the URL
  const decodedUrl = decodeURIComponent(asset.assetUrl);
  // Extract the path portion (before any ? or #)
  const pathOnly = decodedUrl.split(/[?#]/)[0];
  // Get the last segment as the filename
  const filename = pathOnly.split('/').pop() ?? '';

  // Check for pure hex strings or UUID format (system-generated assets)
  const hexRegex = /^[0-9a-fA-F]+$/;
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const filenameWithoutExt = filename.split('.')[0];

  if (hexRegex.test(filenameWithoutExt) || uuidRegex.test(filenameWithoutExt)) {
    return <span style={{ fontStyle: 'italic' }}>{t('asset_filename_system_generated')}</span>;
  }

  return <span>{filename}</span>;
};
